import { useEffect, useRef, useState } from "react";
import { X, Loader2, ImageIcon, Globe } from "lucide-react";
import { toast } from "sonner";
import { mistApi, streamAsset, thumbAsset, posterAsset, fetchAssetFile, pipelinesApi } from "@/api/mist";
import { profileGalleryApi, streamProfileMedia } from "@/api/profileGallery";

// Mist Library is retired. This picker now sources the user's private PROFILE
// GALLERY (profile_media) and, for images only, a public COMMUNITY SHARE tab.
// It still hands callers exactly what they expect:
//   • onFile(file)  — a real File (device-upload handlers work unchanged).
//   • onItem({id})  — a durable account_assets id (profile items are uploaded,
//                     community items are cloned via communityUse) so pipeline
//                     references + streamAsset previews resolve.
// Audio has no Profile Gallery / Community image source and shows empty.

function Thumb({ item, loader, isVideo, onPick }) {
  const [src, setSrc] = useState(null);
  const [near, setNear] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) { setNear(true); io.disconnect(); } }, { rootMargin: "300px" });
    io.observe(el); return () => io.disconnect();
  }, []);
  useEffect(() => {
    if (!near) return; let u; let dead = false;
    loader(item.id).then((x) => { if (dead) { URL.revokeObjectURL(x); return; } u = x; setSrc(x); }).catch(() => {});
    return () => { dead = true; if (u) URL.revokeObjectURL(u); };
  }, [near, item.id]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <button ref={ref} onClick={() => onPick(item)} data-testid={`vaultpick-item-${item.id}`}
      className="relative aspect-square border-2 border-black bg-charcoal overflow-hidden active:translate-y-[1px]">
      {src
        ? (isVideo ? <video src={src} muted playsInline className="w-full h-full object-cover" /> : <img src={src} alt={item.filename || "asset"} loading="lazy" className="w-full h-full object-cover" />)
        : <div className="grid h-full w-full place-items-center"><Loader2 size={16} className="animate-spin text-white/40" /></div>}
      {isVideo && <span className="absolute bottom-0.5 right-0.5 bg-black/70 text-[#00F5D4] text-[7px] font-mono font-black px-1 py-0.5 uppercase">▶ vid</span>}
    </button>
  );
}

function PickerSheet({ bucket, title, onClose, onPicked }) {
  const isVideo = bucket === "videos";
  const isImage = bucket === "images";
  const [tab, setTab] = useState("profile");            // profile | community (images only)
  const [profile, setProfile] = useState(null);
  const [community, setCommunity] = useState(null);

  useEffect(() => {
    profileGalleryApi.media(0)
      .then((d) => setProfile(isVideo ? (d.videos || []) : (d.images || [])))
      .catch(() => setProfile([]));
  }, [bucket]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (tab !== "community" || community !== null) return;
    mistApi.communityVault().then((d) => setCommunity(d.images || [])).catch(() => setCommunity([]));
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const list = tab === "community" ? community : profile;
  const loader = tab === "community" ? ((id) => thumbAsset(id).catch(() => streamAsset(id))) : streamProfileMedia;

  return (
    <div className="fixed inset-0 z-[300] bg-black/80 flex items-end justify-center" data-testid="vaultpick-modal" onClick={onClose}>
      <div className="w-full max-w-md bg-[#0d0d0d] border-t-4 border-[#00F5D4] rounded-t-3xl p-4 max-h-[80dvh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 pb-3 mb-3 border-b-2 border-[#00F5D4]/20">
          <h3 className="flex-1 font-serif text-xl text-white">{title}</h3>
          <button onClick={onClose} data-testid="vaultpick-close" className="w-8 h-8 grid place-items-center border-2 border-[#00F5D4] text-[#00F5D4]"><X size={15} /></button>
        </div>
        {isImage && (
          <div className="flex gap-1.5 mb-3" data-testid="vaultpick-tabs">
            <button onClick={() => setTab("profile")} data-testid="vaultpick-tab-profile" className={`flex-1 flex items-center justify-center gap-1.5 border-2 py-2 font-mono text-[9px] font-black uppercase tracking-widest ${tab === "profile" ? "border-[#00F5D4] bg-[#00F5D4] text-black" : "border-[#00F5D4]/40 bg-black text-[#00F5D4]"}`}><ImageIcon size={12} /> Profile Gallery</button>
            <button onClick={() => setTab("community")} data-testid="vaultpick-tab-community" className={`flex-1 flex items-center justify-center gap-1.5 border-2 py-2 font-mono text-[9px] font-black uppercase tracking-widest ${tab === "community" ? "border-[#C9B6FF] bg-[#C9B6FF] text-black" : "border-[#C9B6FF]/40 bg-black text-[#C9B6FF]"}`}><Globe size={12} /> Community Share</button>
          </div>
        )}
        <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar">
          {list == null ? <div className="grid place-items-center py-10"><Loader2 size={20} className="text-[#00F5D4] animate-spin" /></div>
            : list.length === 0 ? <p className="font-mono text-[10px] text-white/50 py-8 text-center">{tab === "community" ? "No shared images yet." : "Nothing in your Profile Gallery yet — save a creation there first."}</p>
              : <div className="grid grid-cols-3 gap-2">{list.map((it) => <Thumb key={it.id} item={it} loader={loader} isVideo={isVideo && tab === "profile"} onPick={(x) => onPicked(x, tab)} />)}</div>}
        </div>
      </div>
    </div>
  );
}

// Strip retired-vault vocabulary from any caller-supplied label/title text.
const scrub = (s) => (s || "").replace(/\bmy vault albums\b|\bcloud library\b|\bmist stock\b|\bvault\b|\blibrary\b/gi, "Profile Gallery").trim();

export default function VaultPickButton({
  onFile, onItem, bucket = "images", label = "From Profile Gallery",
  title = "Your Profile Gallery", className, testid = "vault-pick", disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const displayLabel = label === "" ? "" : (scrub(label) || "Profile Gallery");
  const displayTitle = scrub(title) || "Your Profile Gallery";

  const pick = async (item, source) => {
    setOpen(false); setLoading(true);
    try {
      const ext = bucket === "videos" ? "mp4" : "png";
      if (onItem) {
        // Callers embed the id into pipelines → hand back a durable account_assets id.
        let assetId = item.id;
        if (source === "community") {
          const r = await mistApi.communityUse(item.id); assetId = r.asset_id || r.id || item.id;
        } else {
          const url = await streamProfileMedia(item.id);
          const blob = await (await fetch(url)).blob();
          URL.revokeObjectURL(url);
          const file = new File([blob], item.filename || `${item.id}.${ext}`, { type: blob.type || undefined });
          const up = bucket === "videos" ? await pipelinesApi.uploadSeedVideo(file) : await pipelinesApi.uploadSeed(file);
          assetId = up.asset_id || up.id;
        }
        await onItem({ id: assetId, name: (item.filename || item.title || "asset") });
      }
      if (onFile) {
        let file;
        if (source === "community") {
          file = await fetchAssetFile(item.id, item.filename || `${item.id}.${ext}`);
        } else {
          const url = await streamProfileMedia(item.id);
          const blob = await (await fetch(url)).blob();
          URL.revokeObjectURL(url);
          file = new File([blob], item.filename || `${item.id}.${ext}`, { type: blob.type || undefined });
        }
        await onFile(file);
      }
    } catch { toast.error("Couldn't load that item"); }
    finally { setLoading(false); }
  };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} disabled={disabled || loading} data-testid={testid}
        className={className || "flex items-center justify-center gap-1.5 border-2 border-[#00F5D4] bg-black text-[#00F5D4] px-3 py-2 font-mono text-[9px] font-black uppercase tracking-widest active:translate-y-[1px] disabled:opacity-50"}>
        {loading ? <Loader2 size={12} className="animate-spin" /> : <ImageIcon size={12} />} {displayLabel}
      </button>
      {open && <PickerSheet bucket={bucket} title={displayTitle} onClose={() => setOpen(false)} onPicked={pick} />}
    </>
  );
}
